/**
 * StreamFlow API Client
 *
 * Service layer for the IPTV Family Streaming Dashboard.
 * Uses Supabase session token for auth.
 *
 * Backend: server/ (Express, default port 3006) — set VITE_STREAMFLOW_API_URL
 * in .env to point at a deployed server URL.
 *
 * @module lib/streamFlowApi
 */

import { supabase } from './supabase';
import { getApiBaseMode, getApiBaseUrl } from './apiBase';

// API base is computed per-request from `apiBase.ts` so a runtime
// switch (public ↔ tailnet) takes effect immediately. The dev override
// `VITE_STREAMFLOW_API_URL` still wins when set.

export interface ApiResponse<T> {
  success: boolean;
  data?: T;
  error?: { code: string; message: string; details?: unknown };
  pagination?: { page: number; limit: number; total: number; pages: number };
}

// ============================================================
// Types
// ============================================================

export interface Provider {
  id: string;
  /** Owner's Supabase user id. Compare against the current session's user id
   * to decide whether to render edit/delete affordances. */
  user_id: string;
  name: string;
  provider_type: 'xtream' | 'm3u' | 'jellyfin' | 'ersatztv';
  host: string;
  max_connections: number;
  is_active: boolean;
  health_status: 'healthy' | 'unhealthy' | 'unknown';
  last_health_check: string | null;
  extra_config: Record<string, unknown>;
  /** True for community/admin-shared providers. Shows the "🌐 Community"
   * badge and makes non-owner rows read-only. */
  is_shared: boolean;
  /** Provider-egress pin (2026-05-17). All seats on this provider
   * route through this single proxy node so upstream IPTV credentials
   * are never used from more than one egress IP. NULL = azure-edge. */
  bound_proxy_node_id: string | null;
  created_at: string;
  updated_at: string;
}

export interface MeInfo {
  id: string;
  email: string;
  is_admin: boolean;
}

export interface ProxyNode {
  id: string;
  user_id: string;
  name: string;
  host: string;
  is_tailnet_only: boolean;
  is_default_community: boolean;
  available_for_checkout: boolean;
  is_shared: boolean;
  health_status: 'healthy' | 'unhealthy' | 'unknown';
  last_health_check: string | null;
  extra_config: Record<string, unknown>;
  created_at: string;
  updated_at: string;
}

export interface Seat {
  id: string;
  provider_id: string;
  seat_index: number;
  claimed_by: string | null;
  claimed_at: string | null;
  expires_at: string | null;
  is_checkout: boolean;
  checkout_port: number | null;
  /** Phase E: when checkout picks a non-default proxy node, the chosen
   * sf_proxy_nodes.id is stored here so subsequent /api/stream/url
   * mints route through it. */
  proxy_node_id: string | null;
  device_info: Record<string, unknown>;
  status: 'available' | 'claimed' | 'checkout';
  current_channel_name: string | null;
}

export interface FamilyMember {
  id: string;
  display_name: string;
  family_role: 'admin' | 'member';
  avatar_url: string | null;
  tailscale_device_id: string | null;
  created_at: string;
  updated_at: string;
}

export interface Channel {
  id: string;
  name: string;
  normalized_name: string;
  logo_url: string | null;
  epg_id: string | null;
  category: string | null;
  is_24_7_channel: boolean;
  is_local_media: boolean;
  /** Catalog kind. 'live' = streaming channel, 'vod' = movie,
   * 'series' = TV show (drill into episodes on click). Defaulted to
   * 'live' on rows pre-dating the VOD ingestion (2026-05-31). */
  kind?: 'live' | 'vod' | 'series';
  sf_channel_sources?: Array<{
    id: string;
    provider_id: string;
    provider_stream_id: string;
    stream_type: string;
    priority: number;
  }>;
}

export interface EpgProgram {
  id: string;
  channel_id: string;
  title: string;
  description: string | null;
  start_time: string;
  end_time: string;
  category: string | null;
}

export interface StreamUrl {
  stream_url: string;
  channel_id: string;
  provider_id: string;
  stream_type: string;
  /** Resolution strategy:
   *  - 'tailscale-direct': raw upstream URL (no proxy hop). ErsatzTV-on-tailnet only.
   *  - 'home-proxy': JWT-bound URL pointing at a chosen proxy node (e.g., tk-bakery).
   *  - 'azure-edge': JWT-bound URL pointing at gastv-api.gasweb.info. */
  stream_mode?: 'tailscale-direct' | 'home-proxy' | 'azure-edge';
  /** When mode is 'home-proxy' or a checkout fixed a node, the chosen
   * sf_proxy_nodes.id. The dashboard surfaces this in a "via tk-bakery"
   * badge so viewers can see which machine is serving them. */
  proxy_node_id?: string | null;
  /** Provider ids the client can fall back to (in priority order) if the
   * current source's playback fails. Used by the Phase G mirror failover
   * loop to call /api/stream/url again with a different preferred_provider_id. */
  available_provider_ids?: string[];
}

export interface LibraryItem {
  id: string;
  name: string;
  /** Jellyfin item type — 'Movie' or 'Series' (Episode is filtered out
   * server-side for the library browser). */
  type: string;
  overview: string | null;
  year: number | null;
  /** Same-origin URL pointing at the StreamFlow poster proxy
   * (`/api/providers/:id/library/:itemId/poster`). Includes the
   * Authorization header automatically when set as <img src>… EXCEPT it
   * doesn't, so the API base host must accept cookie-less GET — which
   * it does because the route is auth-required at the user level but
   * the browser will still need the bearer token. See Library.tsx for
   * the blob fetch fallback. */
  poster_url: string;
  runtime_minutes: number | null;
}

export interface LibraryPlayResponse {
  stream_url: string;
  expires_at: string;
  stream_mode?: 'tailscale-direct' | 'home-proxy' | 'azure-edge';
  proxy_node_id?: string | null;
}

export interface WatchHistoryEntry {
  id: string;
  member_id: string;
  channel_id: string;
  provider_id: string | null;
  device: string | null;
  started_at: string;
  ended_at: string | null;
  channel?: { id: string; name: string; logo_url: string | null };
}

export interface XtreamCredential {
  id: string;
  member_id: string;
  username: string;
  label: string | null;
  is_active: boolean;
  last_used_at: string | null;
  last_used_ip: string | null;
  expires_at: string | null;
  created_at: string;
}

export interface XtreamCredentialMint extends XtreamCredential {
  /** Plaintext password — only present in the create response. Never
   * returned again. */
  password: string;
}

export interface FavoriteEntry {
  id: string;
  member_id: string;
  channel_id: string;
  created_at: string;
  channel?: { id: string; name: string; logo_url: string | null; category: string | null };
}

// ============================================================
// HTTP helpers
// ============================================================

async function getToken(): Promise<string | null> {
  const { data: { session } } = await supabase.auth.getSession();
  return session?.access_token ?? null;
}

const HOUSEHOLD_STORAGE_KEY = 'streamflow.activeHouseholdId';

/** Read the SPA's currently-selected household id from localStorage. The
 * household context writes this on switch; the API client reads it on
 * every request and pins the X-Household-Id header so the server scopes
 * tenant-aware queries (sf_providers, sf_family_members, sf_channels)
 * to that household. Returns null on first load — server falls back to
 * single-membership shortcut. */
export function getActiveHouseholdId(): string | null {
  try {
    return localStorage.getItem(HOUSEHOLD_STORAGE_KEY);
  } catch {
    return null;
  }
}

export function setActiveHouseholdId(id: string | null): void {
  try {
    if (id) localStorage.setItem(HOUSEHOLD_STORAGE_KEY, id);
    else localStorage.removeItem(HOUSEHOLD_STORAGE_KEY);
  } catch {
    /* ignore storage failures (private mode etc.) */
  }
}

async function request<T>(path: string, options: RequestInit = {}): Promise<ApiResponse<T>> {
  const token = await getToken();
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(options.headers as Record<string, string>),
  };
  if (token) headers['Authorization'] = `Bearer ${token}`;
  const householdId = getActiveHouseholdId();
  if (householdId) headers['X-Household-Id'] = householdId;
  // Defensive hint to the resolver: if the user has opted into tailnet
  // routing on this device, advertise it. The server only honors this
  // header when the request actually arrived on the tailnet hostname,
  // so an attacker can't spoof it against gastv-api.gasweb.info.
  if (getApiBaseMode() === 'tailnet') headers['X-Client-On-Tailnet'] = '1';

  const apiBase = getApiBaseUrl();
  try {
    let res = await fetch(`${apiBase}${path}`, {
      ...options,
      headers,
      signal: options.signal ?? AbortSignal.timeout(10000),
    });

    if (res.status === 401) {
      const { data: refreshData } = await supabase.auth.refreshSession();
      const newToken = refreshData.session?.access_token;
      if (newToken) {
        headers['Authorization'] = `Bearer ${newToken}`;
        res = await fetch(`${apiBase}${path}`, { ...options, headers });
      }
    }

    const contentType = res.headers.get('content-type') || '';
    if (!contentType.includes('application/json')) {
      return {
        success: false,
        error: { code: 'INVALID_RESPONSE', message: `Server returned non-JSON (${res.status}). Backend may not be running.` },
      };
    }

    const json = await res.json();
    if (!res.ok) {
      return { success: false, error: json.error ?? { code: 'UNKNOWN', message: res.statusText } };
    }
    return json;
  } catch (err) {
    return {
      success: false,
      error: { code: 'NETWORK_ERROR', message: err instanceof Error ? err.message : 'Network error' },
    };
  }
}

function get<T>(path: string, headers?: Record<string, string>) {
  return request<T>(path, { method: 'GET', headers });
}
function post<T>(path: string, body?: unknown, headers?: Record<string, string>) {
  return request<T>(path, { method: 'POST', body: JSON.stringify(body), headers });
}
function put<T>(path: string, body?: unknown, headers?: Record<string, string>) {
  return request<T>(path, { method: 'PUT', body: JSON.stringify(body), headers });
}
function del<T>(path: string, headers?: Record<string, string>) {
  return request<T>(path, { method: 'DELETE', headers });
}

async function withFallback<T>(
  backendCall: Promise<ApiResponse<T>>,
  fallbackCall: () => Promise<{ data: T | null; error: unknown }>,
): Promise<ApiResponse<T>> {
  const backendRes = await backendCall;
  if (backendRes.success) return backendRes;

  const { data, error } = await fallbackCall();
  if (error) {
    return { success: false, error: { code: 'DATABASE_ERROR', message: 'Failed to fetch from database' } };
  }
  return { success: true, data: data as T };
}

// ============================================================
// API methods
// ============================================================

export interface Household {
  id: string;
  name: string;
  owner_user_id: string;
  /** The caller's role in this household: 'owner' | 'admin' | 'member'.
   * Mutating provider/family/proxy resources requires owner or admin. */
  role: 'owner' | 'admin' | 'member';
  subscription_status: 'inactive' | 'trialing' | 'active' | 'past_due' | 'canceled';
  trial_ends_at: string | null;
  current_period_end: string | null;
  created_at: string;
}

export interface HouseholdMember {
  user_id: string;
  role: 'owner' | 'admin' | 'member';
  email: string | null;
  created_at: string;
}

export const streamFlowApi = {
  // --- Identity ---
  getMe: () => get<MeInfo>('/me'),

  // --- Households (multi-tenant) ---
  listHouseholds: () => get<Household[]>('/households'),
  createHousehold: (name: string) => post<Household>('/households', { name }),
  renameHousehold: (id: string, name: string) => put<Household>(`/households/${id}`, { name }),
  listHouseholdMembers: (id: string) => get<HouseholdMember[]>(`/households/${id}/members`),
  addHouseholdMember: (id: string, email: string, role: 'admin' | 'member' = 'member') =>
    post<HouseholdMember>(`/households/${id}/members`, { email, role }),
  removeHouseholdMember: (id: string, userId: string) =>
    del<void>(`/households/${id}/members/${userId}`),
  updateHouseholdMemberRole: (id: string, userId: string, role: 'admin' | 'member') =>
    put<HouseholdMember>(`/households/${id}/members/${userId}`, { role }),
  /** Change a household member's role. Owner only (enforced server-side).
   * Hits PUT /api/households/:id/members/:userId — the owner can't change
   * their own role here (the server rejects it; role transfers go through
   * a separate flow). Returns the updated `{ user_id, role }`. */
  updateHouseholdMember: (householdId: string, userId: string, patch: { role: 'admin' | 'member' }) =>
    put<HouseholdMember>(`/households/${householdId}/members/${userId}`, patch),

  // --- Household invites ---
  createHouseholdInvite: (householdId: string, email: string, role: 'admin' | 'member' = 'member') =>
    post<{
      id: string;
      household_id: string;
      invited_email: string;
      role: 'admin' | 'member';
      created_at: string;
      expires_at: string;
      url: string;
    }>(`/households/${householdId}/invites`, { email, role }),
  listHouseholdInvites: (householdId: string) =>
    get<Array<{
      id: string;
      invited_email: string;
      role: 'admin' | 'member';
      created_at: string;
      expires_at: string;
      url?: string;
    }>>(`/households/${householdId}/invites`),
  revokeHouseholdInvite: (householdId: string, inviteId: string) =>
    del<void>(`/households/${householdId}/invites/${inviteId}`),
  /** Recipient-side: preview an invite by token (auth required, no
   * household scope). Used by /invite/:token to render the household
   * name + role before the user clicks Accept. */
  getInviteByToken: (token: string) =>
    get<{
      household_id: string;
      household_name: string;
      invited_email: string;
      role: 'admin' | 'member';
      status: 'pending' | 'accepted' | 'revoked' | 'expired';
      expires_at: string;
    }>(`/invites/${encodeURIComponent(token)}`),
  acceptInvite: (token: string) =>
    post<{ household_id: string; role: 'admin' | 'member' }>(`/invites/${encodeURIComponent(token)}/accept`),

  // --- TV pairing (Shield / Fire TV login) ---
  /** TV-side: create a fresh pairing code. Returns both the 6-char
   * `code` (shown to the user, typed into their phone) and a 32-byte
   * `device_secret` (must be held in memory by this TV and sent on
   * every poll/redeem via X-Device-Secret — proves the polling client
   * is the same device that minted the code). */
  createPairing: (deviceLabel?: string) =>
    post<{ code: string; device_secret: string; expires_at: string; ttl_minutes: number; poll_url: string }>(
      '/pairings',
      deviceLabel ? { device_label: deviceLabel } : {},
    ),
  /** TV-side: poll until the phone activates. Requires the
   * `deviceSecret` returned by createPairing — the server returns
   * redemption_token only when the secret matches. */
  pollPairingStatus: (code: string, deviceSecret: string) =>
    get<{
      status: 'pending' | 'activated' | 'expired' | 'redeemed';
      device_label?: string | null;
      expires_at?: string;
      redemption_token?: string;
    }>(
      `/pairings/${encodeURIComponent(code)}/status`,
      { 'X-Device-Secret': deviceSecret },
    ),
  /** Phone-side: approve a code shown on the TV. Auth required —
   * server attaches the caller's user_id as activated_by. The phone
   * does NOT have the device_secret (only the TV does). */
  activatePairing: (code: string, deviceLabel?: string) =>
    post<{ device_label: string | null }>(
      `/pairings/${encodeURIComponent(code)}/activate`,
      deviceLabel ? { device_label: deviceLabel } : {},
    ),
  /** TV-side: exchange the redemption_token for a Supabase session.
   * Requires the `deviceSecret` returned by createPairing.
   * One-shot — a second redeem returns 410. */
  redeemPairing: (code: string, redemptionToken: string, deviceSecret: string) =>
    post<{
      access_token: string;
      refresh_token: string;
      expires_in: number;
      token_type: string;
      user: { id: string; email: string };
    }>(
      `/pairings/${encodeURIComponent(code)}/redeem`,
      { redemption_token: redemptionToken },
      { 'X-Device-Secret': deviceSecret },
    ),

  // --- Billing ---
  /** Get the active household's subscription state. Used to render the
   * banner ("Trial ends in 3 days") and decide whether to show
   * Subscribe vs Manage billing. */
  getBillingStatus: () => get<{
    id: string;
    name: string;
    subscription_status: 'inactive' | 'trialing' | 'active' | 'past_due' | 'canceled';
    trial_ends_at: string | null;
    current_period_end: string | null;
    stripe_configured: boolean;
    billing_required: boolean;
  }>('/billing/status'),
  /** Owner only — opens a Stripe Checkout Session for the $15/mo plan. */
  startCheckoutSession: () => post<{ url: string }>('/billing/checkout-session'),
  /** Owner only — opens the Stripe Customer Portal for the household. */
  openBillingPortal: () => post<{ url: string }>('/billing/portal'),
  /** Is the card→crypto (MoonPay) prepay path enabled on the server + its price/tiers. */
  getCryptoStatus: () => get<{
    enabled: boolean;
    price_per_month_usd: number;
    currency_code: string;
    months_options: number[];
  }>('/billing/crypto/status'),
  /** Owner only — returns a hosted MoonPay URL: pay by card, settles crypto to us,
   *  prepays `months`. Redirect the browser to the returned url. */
  startCryptoCheckout: (months: number) =>
    post<{ url: string; ref: string; months: number; amount_usd: number }>('/billing/crypto-checkout', { months }),

  // --- Admin (operator-only, gated server-side on isAdminEmail) ---
  adminCostSummary: (days = 30) =>
    get<{
      households: Array<{
        household_id: string;
        household_name: string;
        subscription_status: string;
        gb: number;
        cost_usd: number;
        requests: number;
        monthly_revenue_usd: number;
      }>;
      totals: { gb: number; cost_usd: number; revenue_usd: number; households: number };
    }>(`/admin/cost-summary?days=${days}`),
  adminListSharingAlerts: (includeResolved = false) =>
    get<Array<{
      id: string;
      household_id: string;
      distinct_devices: number;
      detected_at: string;
      resolved: boolean;
      resolved_at: string | null;
      notes: string | null;
      sf_households: { name: string; owner_user_id: string } | null;
    }>>(`/admin/sharing-alerts${includeResolved ? '?include_resolved=true' : ''}`),
  adminResolveSharingAlert: (id: string, notes?: string) =>
    post<void>(`/admin/sharing-alerts/${id}/resolve`, notes ? { notes } : {}),
  // Live "who is streaming what" — currently-claimed seats.
  adminActiveStreams: () =>
    get<Array<{
      seat_id: string;
      member: string;
      household: string;
      household_id: string | null;
      channel: string;
      provider: string;
      node: string;
      since: string | null;
      expires: string | null;
      device: string | null;
      status: string;
    }>>(`/admin/active-streams`),
  // Per-customer drill-down for troubleshooting.
  adminHousehold: (id: string) =>
    get<{
      household: { id: string; name: string; subscription_status: string; subscription_method: string | null; current_period_end: string | null; owner_user_id: string };
      providers: Array<{ id: string; name: string; provider_type: string; health_status: string; health_detail: string | null; last_health_check: string | null; max_connections: number }>;
      members: Array<{ id: string; display_name: string }>;
      seats: Array<{ seat_index: number; current_channel_name: string | null; claimed_at: string | null; expires_at: string | null; status: string; provider_id: string | null }>;
      recent_watches: Array<{ started_at: string; position_seconds: number | null; duration_seconds: number | null; channel_id?: string | null; sf_channels: { id?: string; name: string; kind: string } | null }>;
      devices: Array<{ ip_prefix: string; ua_hash: string; hit_count: number; first_seen_at: string; last_seen_at: string }>;
      pairings: Array<{ code: string; device_label: string | null; activated_at: string | null; redeemed_at: string | null }>;
    }>(`/admin/household/${id}`),

  // --- Admin GA Track B: support-console actions (every mutation audited
  // server-side in sf_admin_audit; all gated on isAdminEmail). ---

  /** Paginated admin action trail, newest first. */
  adminAuditLog: (limit = 50, offset = 0) =>
    get<Array<{
      id: string;
      admin_email: string | null;
      admin_user_id: string | null;
      action: string;
      target_type: string | null;
      target_id: string | null;
      detail: Record<string, unknown> | null;
      created_at: string;
    }>>(`/admin/audit-log?limit=${limit}&offset=${offset}`),

  /** Kill-switch a seat: disconnect active streams + clear cached refs. */
  adminRevokeSeat: (seatId: string) =>
    post<{ seat_id: string; refs_cleared: number }>(`/admin/seats/${seatId}/revoke`),

  /** Admin-scope provider edit (any provider by id; not household-scoped).
   * Credential fields are encrypted server-side and never echoed back. */
  adminUpdateProvider: (
    id: string,
    data: Partial<{ name: string; provider_type: string; host: string; username: string; password: string; user_agent: string; max_connections: number; extra_config: Record<string, unknown> }>,
  ) => put<Provider>(`/admin/providers/${id}`, data),

  /** Fire-and-forget re-sync of a provider's channel catalog. */
  adminResyncProvider: (id: string) =>
    post<{ provider_id: string; status: string }>(`/admin/providers/${id}/resync`),

  /** Probe one channel's mirrors so an operator can tell "channel is dead
   * upstream" from "bug on our end". Returns, per source/provider, the
   * stored health_status, the egress it routes through, the live HTTP
   * probe status, and whether that mirror is currently alive. `any_alive`
   * summarizes whether the channel is playable at all right now. */
  adminProbeChannel: (channelId: string) =>
    post<{
      channel_id: string;
      channel_name: string;
      mirrors: Array<{
        provider_id: string;
        provider_name: string;
        priority: number;
        health_status: string;
        egress: string;
        probe_status: number | null;
        alive: boolean;
      }>;
      any_alive: boolean;
    }>(`/admin/channels/${channelId}/probe`),

  /** Green/red health board: all proxy nodes + all providers. */
  adminStatus: () =>
    get<{
      proxy_nodes: Array<{
        id: string; name: string; host: string;
        is_tailnet_only: boolean; is_default_community: boolean; is_shared: boolean;
        health_status: string; last_health_check: string | null;
      }>;
      providers: Array<{
        id: string; name: string; provider_type: string; host: string;
        is_active: boolean; is_shared: boolean; household_id: string | null;
        health_status: string; health_detail: string | null; last_health_check: string | null;
      }>;
    }>('/admin/status'),

  /** Live egress board: nodes + per-provider chain + current active hop. */
  adminEgress: () =>
    get<{
      nodes: Array<{
        id: string; name: string; host: string; health_status: string;
        last_health_check: string | null; is_tailnet_only: boolean;
        bound_provider_count: number; active_stream_count: number;
      }>;
      azure_edge: { active_stream_count: number };
      providers: Array<{
        id: string; name: string; health_status: string;
        egress_chain: string[] | null; active_egress: string; shifted: boolean;
        active_stream_count: number;
      }>;
      failover_summary: string[];
    }>('/admin/egress'),

  /** Per-channel reliability (telemetry + source health), worst-first. */
  adminChannelHealth: () =>
    get<{
      channels: Array<{
        channel_id: string; name: string;
        reliability_24h: number | null; reliability_7d: number | null;
        fatal_24h: number; rebuffer_24h: number; failover_24h: number;
        sources: Array<{
          provider_id: string; provider_name: string; health_status: string;
          consecutive_failures: number; last_failure_at: string | null; last_failure_status: number | null;
        }>;
      }>;
    }>('/admin/channel-health'),

  adminChannelHealthDetail: (channelId: string) =>
    get<{
      channel: { id: string; name: string };
      hourly: Array<{ hour: string; play_success: number; fatal: number; failover: number; rebuffer: number }>;
      sources: Array<{ provider_id: string; provider_name: string; health_status: string; last_failure_at: string | null; last_failure_status: number | null }>;
      recent_events: Array<{ event_type: string; provider_id: string | null; error_code: string | null; ts: string }>;
    }>(`/admin/channel-health/${channelId}`),

  /** Operator alerts (node flips, dead channels, error spikes). */
  adminListAlerts: () =>
    get<Array<{
      id: string; alert_type: string; severity: string;
      target_type: string | null; target_id: string | null;
      title: string; detail: Record<string, unknown> | null;
      acknowledged_at: string | null; resolved: boolean; created_at: string;
    }>>('/admin/alerts'),
  adminAckAlert: (id: string) => post<{ ok: boolean }>(`/admin/alerts/${id}/ack`),
  adminResolveAlert: (id: string) => post<{ ok: boolean }>(`/admin/alerts/${id}/resolve`),

  /** Subscription snapshot for a household (the comp confirm UI reads this). */
  adminHouseholdBilling: (id: string) =>
    get<{
      id: string;
      name: string;
      subscription_status: string;
      subscription_method: string | null;
      subscription_provider_ref: string | null;
      current_period_end: string | null;
      trial_ends_at: string | null;
    }>(`/admin/household/${id}/billing`),

  /** Manual subscription grant (method='comp'): add `months` and/or `days` of
   * access, extending from the later of now and the existing period end. */
  adminGrantSubscription: (id: string, grant: { months?: number; days?: number }) =>
    post<{ household_id: string; months: number; days: number; current_period_end: string }>(
      `/admin/household/${id}/comp`,
      grant,
    ),
  /** Manual subscription revoke: cancel + expire now so the TVs hit the lapse
   * screen. Reversible via a later grant. */
  adminRevokeSubscription: (id: string, reason?: string) =>
    post<{ household_id: string; status: string }>(
      `/admin/household/${id}/revoke-subscription`,
      { reason },
    ),
  /** Push a real-time toast to a household's TVs over the control channel. */
  adminPushMessage: (id: string, text: string, level?: 'info' | 'warn') =>
    post<{ household_id: string; reached: number }>(`/admin/household/${id}/push-message`, { text, level }),
  /** Force a household's TVs back to the pair screen (clears their session). */
  adminForceRepair: (id: string, reason?: string) =>
    post<{ household_id: string; reached: number }>(`/admin/household/${id}/force-repair`, { reason }),
  /** WS6 — start a consent-gated remote support session; returns the session id
   *  to open /ws/support with. `reached` = devices asked for consent. */
  adminSupportStart: (householdId: string) =>
    post<{ session_id: string; reached: number }>(`/admin/household/${householdId}/support/start`),
  /** WS6 — end a remote support session. */
  adminSupportStop: (sessionId: string) =>
    post<{ session_id: string }>(`/admin/support/${sessionId}/stop`),

  // --- Providers ---
  listProviders: () => withFallback(
    get<Provider[]>('/providers'),
    // Fallback: direct Supabase. RLS already enforces (own OR shared) on
    // SELECT, so a plain SELECT returns the right rows for any signed-in
    // user — owned providers and every is_shared community provider.
    () => supabase.from('sf_providers').select('id, user_id, name, provider_type, host, max_connections, is_active, health_status, last_health_check, extra_config, is_shared, created_at, updated_at').order('created_at', { ascending: false }) as any,
  ),

  createProvider: (data: { name: string; provider_type: string; host: string; username?: string; password?: string; max_connections?: number; is_shared?: boolean; bound_proxy_node_id?: string | null }) =>
    post<Provider>('/providers', data),

  updateProvider: (id: string, data: Partial<Provider>) =>
    put<Provider>(`/providers/${id}`, data),

  deleteProvider: (id: string) =>
    del<void>(`/providers/${id}`),

  checkProviderHealth: (id: string) =>
    post<{ health_status: string }>(`/providers/${id}/health-check`),

  // --- Jellyfin library (server-proxied; X-Emby-Token stays server-side) ---
  listLibraryItems: (providerId: string) =>
    get<{ items: LibraryItem[] }>(`/providers/${providerId}/library`),

  playLibraryItem: (providerId: string, itemId: string, seatId?: string) =>
    post<LibraryPlayResponse>(
      `/providers/${providerId}/library/${encodeURIComponent(itemId)}/play`,
      seatId ? { seat_id: seatId } : {},
    ),

  // --- Proxy nodes (registry of where streaming traffic egresses from) ---
  listProxyNodes: () => get<ProxyNode[]>('/proxy-nodes'),

  getProxyNode: (id: string) => get<ProxyNode>(`/proxy-nodes/${id}`),

  createProxyNode: (data: {
    name: string;
    host: string;
    is_tailnet_only?: boolean;
    is_default_community?: boolean;
    available_for_checkout?: boolean;
    is_shared?: boolean;
    extra_config?: Record<string, unknown>;
  }) => post<ProxyNode>('/proxy-nodes', data),

  updateProxyNode: (id: string, data: Partial<ProxyNode>) =>
    put<ProxyNode>(`/proxy-nodes/${id}`, data),

  deleteProxyNode: (id: string) => del<void>(`/proxy-nodes/${id}`),

  checkProxyNodeHealth: (id: string) =>
    post<{ healthy: boolean }>(`/proxy-nodes/${id}/health-check`),

  /**
   * Pull channels from a provider (or all providers when providerId is
   * omitted) into the unified guide. Auto-runs on provider create; this
   * is the manual re-sync button.
   */
  syncChannels: (providerId?: string) =>
    post<{ channels_synced?: number; total?: number; byProvider?: Record<string, number> }>(
      '/guide/sync',
      providerId ? { provider_id: providerId } : {},
    ),

  // --- Seats ---
  listSeats: () => withFallback(
    get<Seat[]>('/seats'),
    () => supabase.from('sf_seats').select('*').order('provider_id').order('seat_index') as any,
  ),

  claimSeat: (data: { provider_id: string; member_id: string; device_info?: Record<string, unknown>; channel_name?: string }) =>
    post<Seat>('/seats/claim', data),

  /** Swap the member's primary provider — releases any existing seat
   *  and claims one on `new_provider_id`. Single-seat invariant per
   *  docs/SEAT_MODEL.md. */
  swapSeat: (data: { member_id: string; new_provider_id: string; device_info?: Record<string, unknown> }) =>
    post<Seat>('/seats/swap', data),

  releaseSeat: (seatId: string, memberId: string) =>
    del<void>(`/seats/${seatId}?member_id=${memberId}`),

  heartbeat: (data: { seat_id: string; member_id: string; channel_name?: string }) =>
    post<void>('/seats/heartbeat', data),

  checkoutSeat: (data: { provider_id: string; member_id: string; duration_hours?: number; proxy_node_id?: string }) =>
    post<Seat>('/seats/checkout', data),

  // --- Family Members ---
  listMembers: () => withFallback(
    get<FamilyMember[]>('/members'),
    () => supabase.from('sf_family_members').select('id, display_name, family_role, avatar_url, tailscale_device_id, created_at, updated_at').order('display_name') as any,
  ),

  createMember: (data: { display_name: string; family_role?: string; pin?: string; avatar_url?: string }) =>
    post<FamilyMember>('/members', data),

  updateMember: (id: string, data: Partial<FamilyMember>) =>
    put<FamilyMember>(`/members/${id}`, data),

  deleteMember: (id: string) =>
    del<void>(`/members/${id}`),

  verifyPin: (id: string, pin: string) =>
    post<{ verified: boolean }>(`/members/${id}/verify-pin`, { pin }),

  // --- Guide ---
  listChannels: (params?: { category?: string; search?: string; type?: string; page?: number; limit?: number }) => {
    const query = new URLSearchParams();
    if (params?.category) query.set('category', params.category);
    if (params?.search) query.set('search', params.search);
    if (params?.type) query.set('type', params.type);
    if (params?.page) query.set('page', String(params.page));
    if (params?.limit) query.set('limit', String(params.limit));
    const qs = query.toString();
    return get<Channel[]>(`/guide/channels${qs ? '?' + qs : ''}`);
  },

  listCategories: () => get<Array<{ name: string; count: number }>>('/guide/categories'),

  /** Region-aware variant of the categories endpoint. Returns one row
   * per (region × bucket × subgenre) composite — used by the chip
   * grouping UI to render "🇺🇸 USA News" / "🇦🇱 Albania News" as
   * distinct chips with the household's primary region floated to the
   * top. */
  listCategoriesByRegion: () =>
    get<Array<{ key: string; label: string; bucket: string | null; region: string | null; count: number }>>(
      '/guide/categories?detail=region',
    ),

  /** Update the household's primary_region (admin only). Drives the
   * SPA chip ordering and Tivimate's category sort. */
  setHouseholdPrimaryRegion: (id: string, primary_region: string) =>
    request<Household>(`/households/${id}`, {
      method: 'PATCH',
      body: JSON.stringify({ primary_region }),
    }),

  getChannelEpg: (channelId: string) => get<EpgProgram[]>(`/guide/epg/${channelId}`),

  getWhatsOnNow: () => get<EpgProgram[]>('/guide/now'),

  // --- Stream ---
  getStreamUrl: (data: { channel_id: string; member_id: string; preferred_provider_id?: string }) =>
    post<StreamUrl>('/stream/url', data),

  // --- Xtream-Codes-protocol device credentials (Tivimate / Smarters / OTT / VLC) ---
  listXtreamCredentials: () => get<XtreamCredential[]>('/xtream-credentials'),

  createXtreamCredential: (data: { member_id: string; label?: string; expires_at?: string }) =>
    post<XtreamCredentialMint>('/xtream-credentials', data),

  deleteXtreamCredential: (id: string) =>
    del<void>(`/xtream-credentials/${id}`),

  // --- Short-code redirector (Fire TV Downloader friendly) ---
  createShortCode: (data: { target_url: string; label?: string; ttl_days?: number }) =>
    post<{ code: string; short_url: string; target_url: string; expires_in_days: number }>('/short', data),

  // --- Watch history (direct Supabase; sf_watch_history has member-scoped RLS) ---
  listWatchHistory: async (memberId: string, limit = 50): Promise<ApiResponse<WatchHistoryEntry[]>> => {
    const { data, error } = await supabase
      .from('sf_watch_history')
      .select('id, member_id, channel_id, provider_id, device, started_at, ended_at, channel:sf_channels(id, name, logo_url)')
      .eq('member_id', memberId)
      .order('started_at', { ascending: false })
      .limit(limit);
    if (error) return { success: false, error: { code: 'DB_ERROR', message: error.message } };
    return { success: true, data: (data ?? []) as unknown as WatchHistoryEntry[] };
  },

  recordWatchStart: async (params: { memberId: string; channelId: string; providerId?: string; device?: string }): Promise<ApiResponse<{ id: string }>> => {
    const { data, error } = await supabase
      .from('sf_watch_history')
      .insert([{
        member_id: params.memberId,
        channel_id: params.channelId,
        provider_id: params.providerId ?? null,
        device: params.device ?? null,
      }])
      .select('id')
      .single();
    if (error || !data) return { success: false, error: { code: 'DB_ERROR', message: error?.message ?? 'Insert failed' } };
    return { success: true, data: { id: data.id as string } };
  },

  recordWatchEnd: async (historyId: string): Promise<ApiResponse<void>> => {
    const { error } = await supabase
      .from('sf_watch_history')
      .update({ ended_at: new Date().toISOString() })
      .eq('id', historyId);
    if (error) return { success: false, error: { code: 'DB_ERROR', message: error.message } };
    return { success: true };
  },

  // --- Favorites (direct Supabase; sf_favorites has member-scoped RLS) ---
  listFavorites: async (memberId: string): Promise<ApiResponse<FavoriteEntry[]>> => {
    const { data, error } = await supabase
      .from('sf_favorites')
      .select('id, member_id, channel_id, created_at, channel:sf_channels(id, name, logo_url, category)')
      .eq('member_id', memberId)
      .order('created_at', { ascending: false });
    if (error) return { success: false, error: { code: 'DB_ERROR', message: error.message } };
    return { success: true, data: (data ?? []) as unknown as FavoriteEntry[] };
  },

  addFavorite: async (memberId: string, channelId: string): Promise<ApiResponse<{ id: string }>> => {
    const { data, error } = await supabase
      .from('sf_favorites')
      .upsert({ member_id: memberId, channel_id: channelId }, { onConflict: 'member_id,channel_id' })
      .select('id')
      .single();
    if (error || !data) return { success: false, error: { code: 'DB_ERROR', message: error?.message ?? 'Upsert failed' } };
    return { success: true, data: { id: data.id as string } };
  },

  removeFavorite: async (memberId: string, channelId: string): Promise<ApiResponse<void>> => {
    const { error } = await supabase
      .from('sf_favorites')
      .delete()
      .eq('member_id', memberId)
      .eq('channel_id', channelId);
    if (error) return { success: false, error: { code: 'DB_ERROR', message: error.message } };
    return { success: true };
  },
};

/**
 * Bind a WebSocket to the new server-side token-refresh protocol.
 *
 * The server pings `{type:'token_refresh_request'}` every ~50 minutes; the
 * client must respond with the current Supabase access token (which the SDK
 * has already auto-refreshed) within 30s or the server closes the socket.
 *
 * Call this once per opened socket — typically right after `new WebSocket(...)`
 * succeeds. Safe to call before `onopen` fires; the message handler is
 * registered before any messages arrive.
 */
export function bindWsTokenRefresh(socket: WebSocket): void {
  socket.addEventListener('message', async (event) => {
    let msg: { type?: string };
    try {
      msg = JSON.parse(typeof event.data === 'string' ? event.data : '');
    } catch {
      return;
    }
    if (msg.type !== 'token_refresh_request') return;
    const { data: { session } } = await supabase.auth.getSession();
    const token = session?.access_token;
    if (!token) {
      // No session — let the server time us out (close 4408) and let the
      // outer reconnect loop handle re-auth.
      return;
    }
    try {
      socket.send(JSON.stringify({ type: 'token_refresh', token }));
    } catch {
      /* socket already closing */
    }
  });
}

export default streamFlowApi;
